import { useQuery } from "@tanstack/react-query";

import {
  buildSharedSessionExistsQueryKey,
  buildSharedSessionQueryParams,
  hasSharedSessionTarget,
} from "./shared-session-cache-utils";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";

interface UseSharedSessionExistsOptions {
  workspaceId: number | undefined;
  promptId?: number;
  workflowId?: number;
  skillCollectionId?: string;
  enabled?: boolean;
}

/**
 * Issues HEAD /shared_sessions?prompt_id=X (or workflow_id=X) and returns
 * whether at least one session exists — zero body, no over-fetching.
 *
 * HTTP contract (mirrors the backend HEAD route):
 *   200 → sessions exist
 *   204 → no sessions
 *   403 → access denied (thrown as an error)
 */
export const useSharedSessionExists = ({
  workspaceId,
  promptId,
  workflowId,
  skillCollectionId,
  enabled = true,
}: UseSharedSessionExistsOptions) => {
  const authContext = useAuth();

  return useQuery<boolean>({
    queryKey: buildSharedSessionExistsQueryKey({
      workspaceId,
      promptId,
      workflowId,
      skillCollectionId,
    }),
    enabled:
      enabled &&
      !!workspaceId &&
      hasSharedSessionTarget({
        promptId,
        workflowId,
        skillCollectionId,
      }),
    queryFn: async () => {
      const params = buildSharedSessionQueryParams({
        promptId,
        workflowId,
        skillCollectionId,
      });
      // Cache-bust HEAD probes to avoid stale intermediary/browser responses.
      params.set("_ts", String(Date.now()));

      const response = await fetch(
        `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/shared_sessions?${params}`,
        {
          method: "HEAD",
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${authContext?.userToken}`,
            "Cache-Control": "no-cache, no-store, max-age=0",
            Pragma: "no-cache",
          },
          credentials: "include",
        },
      );

      if (response.status === 403) {
        throw new Error("Access denied");
      }

      // 200 → exists, 204 → none
      return response.status === 200;
    },
  });
};
